import React, { useRef, useState } from 'react';
import { useT } from '../../../i18n/LanguageProvider';
import { engagementService } from '../../../services/request/engagement';
import { generateCardGif } from '../../../utils/gifExporter';

interface Props {
    cardId: string;
    recipientEmail: string;
    /** Selector for the card DOM element to capture. Defaults to '[data-card-viewer]' */
    cardSelector?: string;
    /** When true, download events are NOT logged in analytics (e.g. sender preview) */
    skipAnalytics?: boolean;
    /** Direct URL to the cover image for cover-only download */
    coverImageUrl?: string;
    /** Card title used for the downloaded file name */
    cardTitle?: string;
}

const DownloadButton: React.FC<Props> = ({ cardId, recipientEmail, cardSelector = '#card-capture-area', skipAnalytics = false, coverImageUrl, cardTitle }) => {
    const { t } = useT();
    const sanitizedTitle = cardTitle
        ? cardTitle.replace(/[^a-zA-Z0-9-_ ]/g, '').trim().replace(/\s+/g, '-') || cardId
        : cardId;
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState<'png' | 'pdf' | 'gif' | 'cover' | null>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    /**
     * Returns the outermost card wrapper element. Prefers the #card-capture-area wrapper
     * (which wraps ALL pages) over individual .konvajs-content containers (which only wrap one page).
     */
    const getCardElement = (): HTMLElement | null => {
        const wrapperId = document.querySelector('#card-capture-area');
        if (wrapperId) return wrapperId as HTMLElement;

        const wrapperAttr = document.querySelector('[data-card-viewer="true"]');
        if (wrapperAttr) return wrapperAttr as HTMLElement;

        // Single-page fallback: if there's no outer wrapper, a single Konva stage is fine
        const canvasContainer = document.querySelector('.konvajs-content');
        if (canvasContainer) return canvasContainer as HTMLElement;

        return document.querySelector(cardSelector) as HTMLElement;
    };

    /**
     * Gets ALL non-empty canvases inside #card-capture-area, regardless of visibility.
     * Hidden pages (opacity:0, display:none from React-Spring) still retain their canvas buffer.
     */
    const getAllCardCanvases = (): HTMLCanvasElement[] => {
        const wrapper = document.querySelector('#card-capture-area');
        if (!wrapper) return [];
        const canvases = Array.from(wrapper.querySelectorAll('canvas'));
        return canvases.filter(c => c.width > 0 && c.height > 0);
    };

    /**
     * Checks if a canvas is effectively blank (all white or all same color).
     * Used to skip padding pages added by checkNumOfPages().
     */
    const isCanvasBlank = (canvas: HTMLCanvasElement): boolean => {
        try {
            const ctx = canvas.getContext('2d');
            if (!ctx) return true;

            // Sample a few pixels across the canvas instead of checking every single one
            const w = canvas.width;
            const h = canvas.height;
            const samplePoints = [
                [w * 0.25, h * 0.25],
                [w * 0.5, h * 0.5],
                [w * 0.75, h * 0.75],
                [w * 0.25, h * 0.75],
                [w * 0.75, h * 0.25],
                [w * 0.5, h * 0.25],
                [w * 0.5, h * 0.75],
                [w * 0.1, h * 0.1],
                [w * 0.9, h * 0.9],
            ];

            for (const [x, y] of samplePoints) {
                const pixel = ctx.getImageData(Math.floor(x), Math.floor(y), 1, 1).data;
                // Check if pixel is NOT white (255,255,255) and NOT transparent (alpha 0)
                if (pixel[3] > 0 && (pixel[0] < 250 || pixel[1] < 250 || pixel[2] < 250)) {
                    return false; // Found a non-white pixel, canvas has content
                }
            }
            return true; // All sampled pixels are white/transparent — blank page
        } catch {
            // Canvas is tainted — can't read pixels, assume it has content
            return false;
        }
    };

    /**
     * Captures a single canvas to a data URL at the given scale.
     * Returns null if the canvas is tainted or blank.
     */
    const captureCanvas = (c: HTMLCanvasElement, scale: number): string | null => {
        try {
            const exportCanvas = document.createElement('canvas');
            exportCanvas.width = c.width * scale;
            exportCanvas.height = c.height * scale;
            const ctx = exportCanvas.getContext('2d');
            if (!ctx) return null;

            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
            ctx.drawImage(c, 0, 0, exportCanvas.width, exportCanvas.height);

            const dataUrl = exportCanvas.toDataURL('image/png', 1.0);
            return dataUrl && dataUrl.length > 500 ? dataUrl : null;
        } catch {
            return null; // tainted
        }
    };

    /**
     * Konva canvases get tainted when cross-origin images (S3, CDN, etc.) are drawn onto them
     * without `crossOrigin='anonymous'`. Once tainted, `toDataURL` / `getImageData` throw a
     * SecurityError — which is why every page captures returns null and we end up with an
     * empty PDF. The runtime ViewImage/Rect components don't pass crossOrigin to `useImage`,
     * so we proxy-reload each Konva.Image / fillPatternImage through the backend's
     * `/proxy/image` endpoint (which returns `Access-Control-Allow-Origin: *`) right before
     * the capture, and restore the originals afterwards. This keeps the runtime path
     * untouched while making capture work even when image hosts have no CORS configured.
     */
    const ensureCleanCanvasesForCapture = async (): Promise<() => void> => {
        const wrapper = document.querySelector('#card-capture-area');
        if (!wrapper) return () => {};

        let Konva: any;
        try {
            Konva = (await import('konva')).default;
        } catch {
            return () => {};
        }

        const stages: any[] = Array.isArray(Konva?.stages) ? Konva.stages : [];
        const relevantStages = stages.filter((s: any) => {
            try { return wrapper.contains(s.container()); } catch { return false; }
        });
        if (relevantStages.length === 0) return () => {};

        const apiUrl = process.env.REACT_APP_API_URL || '';
        const restores: Array<() => void> = [];
        const LOAD_TIMEOUT_MS = 5000;

        const isCrossOrigin = (src: string): boolean => {
            if (!src) return false;
            if (src.startsWith('data:') || src.startsWith('blob:')) return false;
            try { return new URL(src, window.location.origin).origin !== window.location.origin; }
            catch { return false; }
        };

        const loadCorsImage = (src: string): Promise<HTMLImageElement> =>
            new Promise((resolve, reject) => {
                const i = new Image();
                let done = false;
                const timer = setTimeout(() => {
                    if (done) return;
                    done = true;
                    reject(new Error('image load timeout: ' + src.slice(0, 80)));
                }, LOAD_TIMEOUT_MS);
                i.crossOrigin = 'anonymous';
                i.onload = () => { if (done) return; done = true; clearTimeout(timer); resolve(i); };
                i.onerror = (e) => { if (done) return; done = true; clearTimeout(timer); reject(e); };
                i.src = src;
            });

        const proxyOf = (src: string): string =>
            `${apiUrl}/proxy/image?url=${encodeURIComponent(src)}`;

        // Try direct first (works when image hosts have CORS), then proxy as backup.
        // Loading is parallel per-stage so one slow image doesn't serialize the whole capture.
        const loadCleanImage = async (originalSrc: string): Promise<HTMLImageElement | null> => {
            try {
                return await loadCorsImage(originalSrc);
            } catch {
                // direct CORS not available — try backend proxy
            }
            if (apiUrl) {
                try {
                    return await loadCorsImage(proxyOf(originalSrc));
                } catch (e) {
                    console.warn('PDF capture: proxy load failed for', originalSrc.slice(0, 80), e);
                }
            }
            return null;
        };

        const isStageTainted = (stage: any): boolean => {
            try {
                const c = stage.toCanvas({ pixelRatio: 1 });
                c.getContext('2d').getImageData(0, 0, 1, 1);
                return false;
            } catch {
                return true;
            }
        };

        for (const stage of relevantStages) {
            if (!isStageTainted(stage)) continue;

            const imageShapes: any[] = typeof stage.find === 'function' ? stage.find('Image') : [];
            const rectShapes: any[] = typeof stage.find === 'function' ? stage.find('Rect') : [];

            type Job = { kind: 'image' | 'pattern'; shape: any; original: HTMLImageElement; src: string };
            const jobs: Job[] = [];
            for (const shape of imageShapes) {
                const currentImg: HTMLImageElement | undefined = shape.image?.();
                if (!currentImg?.src) continue;
                if (currentImg.crossOrigin === 'anonymous') continue;
                if (!isCrossOrigin(currentImg.src)) continue;
                jobs.push({ kind: 'image', shape, original: currentImg, src: currentImg.src });
            }
            for (const shape of rectShapes) {
                const pattern: HTMLImageElement | undefined = shape.fillPatternImage?.();
                if (!pattern?.src) continue;
                if (pattern.crossOrigin === 'anonymous') continue;
                if (!isCrossOrigin(pattern.src)) continue;
                jobs.push({ kind: 'pattern', shape, original: pattern, src: pattern.src });
            }

            const results = await Promise.all(jobs.map(async (job) => ({ job, img: await loadCleanImage(job.src) })));

            for (const { job, img } of results) {
                if (!img) continue;
                try {
                    if (job.kind === 'image') {
                        job.shape.image(img);
                        if (typeof job.shape.clearCache === 'function') job.shape.clearCache();
                        restores.push(() => { try { job.shape.image(job.original); } catch {} });
                    } else {
                        job.shape.fillPatternImage(img);
                        if (typeof job.shape.clearCache === 'function') job.shape.clearCache();
                        restores.push(() => { try { job.shape.fillPatternImage(job.original); } catch {} });
                    }
                } catch (e) {
                    console.warn('PDF capture: image swap failed:', e);
                }
            }

            try { stage.draw(); } catch {}
        }

        return () => {
            for (const fn of restores) {
                try { fn(); } catch {}
            }
            for (const stage of relevantStages) {
                try { stage.draw(); } catch {}
            }
        };
    };

    /**
     * Capture each Konva stage by calling stage.toCanvas(), which produces a FRESH
     * canvas every time. This sidesteps the sticky-taint problem: once a DOM <canvas>
     * has had a cross-origin pixel drawn into it, the browser keeps the taint flag set
     * for that canvas's lifetime, even after redrawing with clean (proxied) images.
     * stage.toCanvas() always allocates a new HTMLCanvasElement, so it has no prior
     * taint to inherit — as long as the current image refs in the stage are clean
     * (which `ensureCleanCanvasesForCapture` ensures), this canvas is exportable.
     */
    const captureStagesAsImages = async (scale: number): Promise<string[] | null> => {
        const wrapper = document.querySelector('#card-capture-area');
        if (!wrapper) return null;

        let Konva: any;
        try { Konva = (await import('konva')).default; } catch { return null; }

        const allStages: any[] = Array.isArray(Konva?.stages) ? Konva.stages : [];
        const stagesInWrapper = allStages.filter((s: any) => {
            try { return wrapper.contains(s.container()); } catch { return false; }
        });
        if (stagesInWrapper.length === 0) return null;

        // Decide if a stage carries any real content by asking Konva, not by sampling pixels.
        // checkNumOfPages() in ViewCardLayout pads a card with empty pages (no images, no
        // textBoxes, no shapes); those padding stages should be skipped. Any stage with at
        // least one drawable shape is real content — including text-only pages, which the
        // old isCanvasBlank pixel sampling would miss when text didn't fall on a sample point.
        const stageHasShapes = (stage: any): boolean => {
            try {
                const layers: any[] = (typeof stage.getChildren === 'function' ? stage.getChildren() : stage.children) || [];
                for (const layer of layers) {
                    const shapes: any[] = (typeof layer.getChildren === 'function' ? layer.getChildren() : layer.children) || [];
                    if (shapes.length > 0) return true;
                }
                return false;
            } catch { return false; }
        };

        const images: string[] = [];
        for (const stage of stagesInWrapper) {
            try {
                if (!stageHasShapes(stage)) continue; // skip padding pages
                const c: HTMLCanvasElement = stage.toCanvas({ pixelRatio: scale });
                if (!c || c.width === 0 || c.height === 0) continue;
                const dataUrl = c.toDataURL('image/png', 1.0);
                if (dataUrl && dataUrl.length > 500) images.push(dataUrl);
            } catch (e) {
                console.warn('PDF capture: stage.toCanvas failed:', e);
            }
        }
        return images.length > 0 ? images : null;
    };

    /**
     * Generates an array of image data URLs — one per card page — by reading ALL
     * Konva canvases (including hidden/opacity:0 pages from React-Spring animations).
     * Skips blank padding pages. Used for multi-page PDF export.
     */
    const generateAllPageImages = async (): Promise<string[]> => {
        const scale = 2;

        // Preferred path: use Konva stages directly. stage.toCanvas() produces fresh,
        // untainted canvases — the only reliable way to recover from sticky DOM taint.
        const fromStages = await captureStagesAsImages(scale);
        if (fromStages) return fromStages;

        // Fallback: iterate DOM canvases directly (legacy path, only works for clean canvases).
        const allCanvases = getAllCardCanvases();
        if (allCanvases.length === 0) return [];

        const images: string[] = [];

        for (const canvas of allCanvases) {
            // Skip blank padding pages (added by checkNumOfPages)
            if (isCanvasBlank(canvas)) continue;

            const img = captureCanvas(canvas, scale);
            if (img) images.push(img);
        }

        return images;
    };

    /**
     * Captures the currently visible pages as a single composite image.
     * Used for PNG download (captures what the user sees right now).
     */
    const generateImageBase64 = async (): Promise<string> => {
        const wrapper = getCardElement();
        if (!wrapper) throw new Error('Card element not found');

        try {
            const canvases = Array.from(wrapper.querySelectorAll('canvas'));

            // Filter to only visible canvases (respects React-Spring opacity/display animations)
            const visibleCanvases = canvases.filter(c => {
                if (c.width === 0 || c.height === 0) return false;

                let parent: HTMLElement | null = c.parentElement;
                while (parent && parent !== wrapper) {
                    const style = window.getComputedStyle(parent);
                    if (style.opacity === '0' || style.display === 'none' || style.visibility === 'hidden') {
                        return false;
                    }
                    parent = parent.parentElement;
                }
                return true;
            });

            if (visibleCanvases.length > 0) {
                let minX = Infinity; let minY = Infinity;
                let maxX = -Infinity; let maxY = -Infinity;

                visibleCanvases.forEach(c => {
                    const cRect = c.getBoundingClientRect();
                    minX = Math.min(minX, cRect.left);
                    minY = Math.min(minY, cRect.top);
                    maxX = Math.max(maxX, cRect.right);
                    maxY = Math.max(maxY, cRect.bottom);
                });

                if (minX !== Infinity) {
                    const scale = 2;
                    const exportCanvas = document.createElement('canvas');
                    exportCanvas.width = (maxX - minX) * scale;
                    exportCanvas.height = (maxY - minY) * scale;
                    const ctx = exportCanvas.getContext('2d');

                    if (ctx) {
                        ctx.fillStyle = '#ffffff';
                        ctx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);

                        visibleCanvases.forEach(c => {
                            const cRect = c.getBoundingClientRect();
                            const relX = (cRect.left - minX) * scale;
                            const relY = (cRect.top - minY) * scale;
                            const cWidth = cRect.width * scale;
                            const cHeight = cRect.height * scale;
                            ctx.drawImage(c, relX, relY, cWidth, cHeight);
                        });

                        const dataUrl = exportCanvas.toDataURL('image/png', 1.0);
                        if (dataUrl && dataUrl.length > 500) {
                            return dataUrl;
                        } else {
                            console.warn('Extracted multi-canvas composite was blank (likely tainted). Falling back...');
                        }
                    }
                }
            }
        } catch (err) {
            console.warn('Native Canvas stitching blocked by CORS taint, falling back to html2canvas:', err);
        }

        // HEAVY FALLBACK: html2canvas
        const html2canvas = (await import('html2canvas')).default;

        const images = wrapper.querySelectorAll('img');
        images.forEach(img => {
            if (!img.crossOrigin && img.src && !img.src.startsWith('data:')) {
                img.setAttribute('crossOrigin', 'anonymous');
            }
        });

        const canvas = await html2canvas(wrapper, {
            useCORS: true,
            allowTaint: false,
            proxy: `${process.env.REACT_APP_API_URL || ''}/proxy/image`,
            scale: 2,
            backgroundColor: '#ffffff',
            logging: true,
            imageTimeout: 15000,
            onclone: (clonedDoc) => {
                const realCanvases = Array.from(wrapper.querySelectorAll('canvas'));
                const clonedCanvases = Array.from(clonedDoc.querySelectorAll('canvas'));

                realCanvases.forEach((realC, i) => {
                    const clonedC = clonedCanvases[i];
                    if (realC && clonedC) {
                        try {
                            const data = realC.toDataURL('image/png');
                            const proxyImg = clonedDoc.createElement('img');
                            proxyImg.src = data;
                            proxyImg.style.cssText = clonedC.style.cssText;
                            proxyImg.width = realC.width;
                            proxyImg.height = realC.height;
                            clonedC.parentNode?.replaceChild(proxyImg, clonedC);
                        } catch (e) {
                            // Canvas is tainted — we CANNOT read its pixels, but removing the
                            // node here produces an empty PDF. Leave it in place so html2canvas
                            // at least preserves layout/dimensions and any non-canvas content.
                            console.warn('Tainted canvas left in clone (cannot extract pixels):', e);
                        }
                    }
                });
            }
        });

        return canvas.toDataURL('image/png', 1.0);
    };

    const downloadPNG = async () => {
        setLoading('png');
        setOpen(false);
        let restoreImages: (() => void) | null = null;
        try {
            restoreImages = await ensureCleanCanvasesForCapture();
            const dataUrl = await generateImageBase64();
            const link = document.createElement('a');
            link.download = `${sanitizedTitle}.png`;
            link.href = dataUrl;
            link.click();
            if (!skipAnalytics) {
                await engagementService.logDownload(cardId, recipientEmail, 'png');
            }
        } catch (err) {
            console.error('PNG download failed:', err);
            alert('Failed to generate PNG image. Some card elements might not support downloading.');
        } finally {
            if (restoreImages) restoreImages();
            setLoading(null);
        }
    };

    /**
     * Converts a PNG data URL to a JPEG data URL at the given quality.
     * JPEG is much smaller than PNG for photo-rich card pages, reducing PDF size
     * and avoiding pixelation caused by oversized PDF dimensions.
     */
    const toJpegDataUrl = (pngDataUrl: string, quality = 0.92): Promise<string> => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
                const c = document.createElement('canvas');
                c.width = img.naturalWidth;
                c.height = img.naturalHeight;
                const ctx = c.getContext('2d');
                if (!ctx) return reject(new Error('no ctx'));
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(0, 0, c.width, c.height);
                ctx.drawImage(img, 0, 0);
                resolve(c.toDataURL('image/jpeg', quality));
            };
            img.onerror = reject;
            img.src = pngDataUrl;
        });
    };

    const downloadPDF = async () => {
        setLoading('pdf');
        setOpen(false);
        let restoreImages: (() => void) | null = null;
        try {
            const { jsPDF } = await import('jspdf');

            // Untaint Konva canvases (proxy-reload cross-origin images) so toDataURL works
            restoreImages = await ensureCleanCanvasesForCapture();

            // Capture ALL card pages (including hidden ones) for a complete multi-page PDF
            const allPageImages = await generateAllPageImages();

            if (allPageImages.length > 0) {
                // Each card page becomes its own PDF page
                let pdf: InstanceType<typeof jsPDF> | null = null;

                for (let i = 0; i < allPageImages.length; i++) {
                    const pngData = allPageImages[i];
                    const img = new Image();
                    img.src = pngData;
                    await new Promise((resolve, reject) => {
                        img.onload = resolve;
                        img.onerror = reject;
                    });

                    // The captured image is 2x scaled — use half dimensions for the PDF page
                    // so the high-res image provides sharp rendering at normal page size.
                    const pageW = img.naturalWidth / 2;
                    const pageH = img.naturalHeight / 2;

                    // Convert to JPEG to dramatically reduce PDF file size
                    const jpegData = await toJpegDataUrl(pngData, 0.92);

                    if (i === 0) {
                        pdf = new jsPDF({
                            orientation: pageW > pageH ? 'landscape' : 'portrait',
                            unit: 'px',
                            format: [pageW, pageH],
                        });
                    } else {
                        pdf!.addPage([pageW, pageH],
                            pageW > pageH ? 'landscape' : 'portrait');
                    }
                    pdf!.addImage(jpegData, 'JPEG', 0, 0, pageW, pageH);
                }

                pdf!.save(`${sanitizedTitle}.pdf`);
            } else {
                // Fallback: all native captures failed (e.g. CORS taint) — use html2canvas single-image
                const pngData = await generateImageBase64();

                const img = new Image();
                img.src = pngData;
                await new Promise((resolve, reject) => {
                    img.onload = resolve;
                    img.onerror = reject;
                });

                const pageW = img.naturalWidth / 2;
                const pageH = img.naturalHeight / 2;
                const jpegData = await toJpegDataUrl(pngData, 0.92);

                const pdf = new jsPDF({
                    orientation: pageW > pageH ? 'landscape' : 'portrait',
                    unit: 'px',
                    format: [pageW, pageH],
                });
                pdf.addImage(jpegData, 'JPEG', 0, 0, pageW, pageH);
                pdf.save(`${sanitizedTitle}.pdf`);
            }

            if (!skipAnalytics) {
                await engagementService.logDownload(cardId, recipientEmail, 'pdf');
            }
        } catch (err) {
            console.error('PDF download failed:', err);
            alert('Failed to generate PDF. Some card elements might not support downloading.');
        } finally {
            if (restoreImages) restoreImages();
            setLoading(null);
        }
    };

    const downloadGIF = async () => {
        setLoading('gif');
        setOpen(false);
        let restoreImages: (() => void) | null = null;
        try {
            restoreImages = await ensureCleanCanvasesForCapture();
            const allCanvases = getAllCardCanvases();
            const contentCanvases = allCanvases.filter(c => !isCanvasBlank(c));

            if (contentCanvases.length === 0) {
                throw new Error('No card pages found to export');
            }

            const blob = await generateCardGif(contentCanvases, {
                frameDuration: 2000,
                quality: 10,
            });

            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.download = `${sanitizedTitle}.gif`;
            link.href = blobUrl;
            link.click();
            URL.revokeObjectURL(blobUrl);

            if (!skipAnalytics) {
                await engagementService.logDownload(cardId, recipientEmail, 'gif');
            }
        } catch (err) {
            console.error('GIF download failed:', err);
            alert('Failed to generate GIF. Some card elements might not support downloading.');
        } finally {
            if (restoreImages) restoreImages();
            setLoading(null);
        }
    };

    const downloadCover = async () => {
        if (!coverImageUrl) {
            alert('Cover image not available for this card.');
            return;
        }
        setLoading('cover');
        setOpen(false);
        try {
            // Fetch the cover image as a blob via proxy to bypass CORS
            const proxyUrl = `${process.env.REACT_APP_API_URL || ''}/proxy/image?url=${encodeURIComponent(coverImageUrl)}`;
            const res = await fetch(proxyUrl);
            if (!res.ok) throw new Error(`Failed to fetch cover: ${res.status}`);
            const blob = await res.blob();

            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.download = `${sanitizedTitle}-cover.png`;
            link.href = blobUrl;
            link.click();
            URL.revokeObjectURL(blobUrl);

            if (!skipAnalytics) {
                await engagementService.logDownload(cardId, recipientEmail, 'png');
            }
        } catch (err) {
            console.error('Cover download failed:', err);
            alert('Failed to download card cover.');
        } finally {
            setLoading(null);
        }
    };

    return (
        <div ref={dropdownRef} className="relative inline-block">
            <button
                className="flex items-center gap-2 px-4 py-2.5 bg-white bg-opacity-90 backdrop-blur-sm text-slate-700 border border-slate-200 rounded-xl font-semibold text-sm cursor-pointer shadow-lg hover:bg-opacity-100 hover:shadow-xl transition-all duration-200 disabled:opacity-60"
                onClick={() => setOpen((o) => !o)}
                disabled={!!loading}
            >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                {loading ? (loading === 'cover' ? 'Preparing cover…' : `Preparing ${loading.toUpperCase()}…`) : t('common.download')}
                <svg className={`w-3 h-3 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
            </button>
            {open && (
                <div className="absolute top-full mt-2 left-1/2 transform -translate-x-1/2 bg-white rounded-xl shadow-xl overflow-hidden z-50 min-w-[200px] border border-slate-100">
                    <button
                        className="flex items-center gap-2 w-full text-left px-4 py-2.5 border-none bg-transparent text-sm text-slate-700 cursor-pointer hover:bg-slate-50 transition-colors duration-100"
                        onClick={downloadPNG}
                    >
                        <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        Download as PNG
                    </button>
                    <button
                        className="flex items-center gap-2 w-full text-left px-4 py-2.5 border-none bg-transparent text-sm text-slate-700 cursor-pointer hover:bg-slate-50 transition-colors duration-100"
                        onClick={downloadPDF}
                    >
                        <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        Download as PDF
                    </button>
                    <button
                        className="flex items-center gap-2 w-full text-left px-4 py-2.5 border-none bg-transparent text-sm text-slate-700 cursor-pointer hover:bg-slate-50 transition-colors duration-100"
                        onClick={downloadGIF}
                    >
                        <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Download as GIF
                    </button>
                    <div className="border-t border-slate-100" />
                    <button
                        className="flex items-center gap-2 w-full text-left px-4 py-2.5 border-none bg-transparent text-sm text-slate-700 cursor-pointer hover:bg-slate-50 transition-colors duration-100"
                        onClick={downloadCover}
                    >
                        <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        Download cover only
                    </button>
                </div>
            )}
        </div>
    );
};

export default DownloadButton;
